import {
  isToday,
} from "date-fns";

import api from "../configs/api";

import {
  Items,
  KitchenOrder,
  KitchenOrders, NewOrder,
  NewOrderIndividual,
} from "../types";

export type CancellationIneligibilityReason =
  | "order_not_found"
  | "order_already_canceled"
  | "order_outside_cancellation_window"
  | string;

export interface CancellationEligibility {
  eligible: boolean;
  reason: CancellationIneligibilityReason | null;
  hasMultipleOrders: boolean;
  orderCount: number;
  transactionId: string | null;
  orderIds: string[];
  totalAmount: number;
}

export const getCancellationEligibility = async (
  orderId: string,
): Promise<CancellationEligibility> => {
  const { status, data } = await api.get<CancellationEligibility>(
    `/client-order/${orderId}/cancellation-eligibility`,
  );

  if (status !== 200) throw new Error();

  return data;
};

export const createOrder = async (newOrder: NewOrder) => {
  try {
    let payload = newOrder;
    if (newOrder.isOnCredit && newOrder.clientId && !newOrder.virtualCard) {
      const { data: client } = await api.get(`/clients/${newOrder.clientId}`);
      if (client.entity?.onCreditDebitsBalanceEnabled) {
        if (client.entity.id !== newOrder.entity || !client.virtualCard?.id) {
          throw new Error("Cliente sem carteira válida nesta entidade");
        }
        payload = { ...newOrder, virtualCard: client.virtualCard.id };
      }
    }
    const { status, data } = await api.post("/client-order", payload);

    if (status !== 201) throw new Error();

    return data;
  } catch (error: any) {
    return { error: true, message: error?.response?.data?.message || "Error creating order" };
  }
};

export const createOrderIndividual = async (newOrder: NewOrderIndividual) => {
  try {
    const { status, data } = await api.post("/virtual-card/payment", newOrder);

    if (status !== 201) throw new Error();

    return data;
  } catch (error) {
    return false;
  }
};

export const getTodaysOrdersPerUser = async (user: string): Promise<KitchenOrders> => {
  const { status, data } = await api.get(
    `/client-order/${user}`
  );

  if (status !== 200) throw new Error();

  const orders = data
    .filter((order: any) => isToday(new Date(order.createdAt)))
    .map((order: any) => {
      const {
        clientName,
        items,
        virtualCard,
        isOnCredit,
        ...rest
      } = order;

      const _items = items.map((item: any) => ({
        ...item.product,
        maxAmount: item.product.quantity,
        quantity: item.quantity,
        price: item.totalAmount,
      }));

      const client = !isOnCredit
        ? virtualCard?.client
        : {
          name: clientName,
          picture: null,
          id: null,
        };

      return {
        ...rest,
        client,
        items: _items,
      } as KitchenOrders;
    });
  return orders;
};

export const getTodayScheduledOrders = async (user: string): Promise<KitchenOrders> => {
  const { status, data } = await api.get(
    `/client-order/schedule/${user}`
  );

  if (status !== 200) throw new Error();

  const orders = data
    .filter((order: any) => isToday(new Date(order.scheduleFor)))
    .map((order: any) => {
      const {
        clientName,
        items,
        virtualCard,
        isOnCredit,
        ...rest
      } = order;

      const _items = items.map((item: any) => ({
        ...item.product,
        maxAmount: item.product.quantity,
        quantity: item.quantity,
        price: item.totalAmount,
      }));

      const client = !isOnCredit
        ? virtualCard?.client
        : {
          name: clientName,
          picture: null,
          id: null,
        };

      return {
        ...rest,
        client,
        items: _items,
      } as KitchenOrders;
    });
  return orders;
};

export const deliverOrder = async (id: string): Promise<boolean> => {
  try {
    const { status } = await api.patch(`/client-order/${id}`, {
      isDelivered: true,
      isCanceled: false,
      isPending: false,
      isInProgress: false,
    });

    if (status !== 200) throw new Error();

    return true;
  } catch (error) {
    return false;
  }
};

export const cancelOrder = async (id: string, items: Items): Promise<boolean> => {
  const { status } = await api.patch(`/client-order/items/${id}`, {
    items
  });

  if (status !== 200) throw new Error();

  return true;
};

export const progressOrder = async (id: string): Promise<KitchenOrder> => {
  const { status, data } = await api.patch(`/client-order/${id}`, {
    isDelivered: false,
    isCanceled: false,
    isPending: false,
    isInProgress: true,
  });

  if (status !== 200) throw new Error();

  return data;
};
